import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import { useGetRealChatId, useUser, v1Client } from '@renderer/chat'
import { useAuth } from '@renderer/chat/providers'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useUpdateTopViewContext } from '../../view-atoms'
import { MEETING_VIEW_ID } from '../../components/meeting/meeting-view-id'
import type { MeetingViewContext, MeetingViewModel } from './use-meeting-view-model'

/**
 * Points this entry at the meeting's chat thread, the one its note (or, while
 * live, its recording) names. A thread with messages becomes the entry's
 * `chatId`. Once it's known to have none, the first chat opens under
 * `newChatId`, which is the recording's id while live and a fresh id for a
 * saved note. A saved note's id may name a chat the user deleted, which the
 * backend refuses to reopen and which reads here the same as an unused id.
 *
 * Disabled, the entry keeps the chats its openers give it, one per ask.
 */
export function useMeetingChatThread(
  context: MeetingViewContext,
  model: Pick<MeetingViewModel, 'variant' | 'audioNote' | 'chatConversationId'>,
  { enabled }: { enabled: boolean },
) {
  const updateViewContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  const isLive = model.variant === 'live'
  const noteChatId = enabled ? model.chatConversationId : undefined
  const hasThread = useHasMessages(context.chatId ? undefined : noteChatId)

  useEffect(() => {
    if (!noteChatId || !hasThread) return
    updateViewContext((prev) => (prev && !prev.chatId ? { ...prev, chatId: noteChatId } : prev))
  }, [noteChatId, hasThread, updateViewContext])

  // The note's PUT is owner-scoped. Local notes carry no userId and have no other owner.
  const user = useUser()
  const note = model.audioNote
  const ownsNote = !!note && (!note.userId || note.userId === user?.id)
  // Live, the recording's own id *is* the thread, and it is known before any
  // read answers. Waiting for one left a window where a send had no id to go
  // to, minted a stray chat of its own and never told the note about it —
  // record, ask, leave, reopen, and the Chat tab was empty. Pinning it early
  // costs nothing: a send on a conversation that already has messages joins it
  // rather than starting a second one.
  const liveChatId = isLive ? noteChatId : undefined
  const needsFreshChatId = enabled && !isLive && ownsNote && (!noteChatId || hasThread === false)
  useEffect(() => {
    if (!liveChatId && !needsFreshChatId) return
    const newChatId = liveChatId ?? uuidv4()
    updateViewContext((prev) => (prev && !prev.chatId && !prev.newChatId ? { ...prev, newChatId } : prev))
  }, [liveChatId, needsFreshChatId, updateViewContext])

  usePointNoteAtNewChat(context, enabled && !isLive ? note : null)
}

/**
 * Undefined until known. Only a 404 means no thread: any other failure, such as
 * a read racing auth on startup, stays unknown and retries, since answering
 * "none" would give the meeting a new chat in place of the one it has.
 */
function useHasMessages(conversationId: string | undefined): boolean | undefined {
  const { getAuthToken } = useAuth()
  const { data } = useQuery({
    queryKey: ['meeting-chat-thread', conversationId],
    queryFn: async () => {
      if (!conversationId) return false
      const { data, error, response } = await v1Client.GET('/conversations/{id}/messages', {
        params: { path: { id: conversationId } },
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      if (response.status === 404) return false
      if (error) throw new Error(`Reading meeting chat ${conversationId} failed with ${response.status}`)
      return (data?.data?.length ?? 0) > 0
    },
    enabled: !!conversationId,
    // A cached answer could name a thread deleted since, so every open asks again.
    gcTime: 0,
  })
  return data
}

/** Once the chat opened under `newChatId` exists, writes it onto the note so the thread outlives this entry. */
function usePointNoteAtNewChat(context: MeetingViewContext, note: MeetingViewModel['audioNote']) {
  const queryClient = useQueryClient()
  const getRealChatId = useGetRealChatId()
  const claimed = useRef<string | undefined>(undefined)
  const chatId = context.chatId ? getRealChatId(context.chatId) : undefined
  const { newChatId } = context

  useEffect(() => {
    if (!note || !newChatId || chatId !== newChatId) return
    if (note.chatConversationId === newChatId || claimed.current === newChatId) return
    claimed.current = newChatId
    AudioNoteIpcRenderer.updateAudioNote({ id: note.id, updates: { chatConversationId: newChatId } })
      .then(() => queryClient.invalidateQueries({ queryKey: ['audio-note', note.id] }))
      .catch((error: unknown) => {
        // The thread still works in this entry. A reopened note just won't find it.
        console.error('Failed to point the meeting note at its chat:', note.id, error)
        claimed.current = undefined
      })
  }, [note, newChatId, chatId, queryClient])
}
